import React from "react";
// TRANSLATION
import { useTranslation } from "react-i18next";
// ANT DESIGN COMPONENTS
import { Select, Switch, Collapse } from "antd";
// FLATBUFFER ENUMS
import { SecurityPolicy } from "../../../../../flatbuffers/generated/opc-ua/security-policy";
import { MessageSecurityMode } from "../../../../../flatbuffers/generated/opc-ua/message-security-mode";
// COMPONENTS
import FormInputText from "../../../../components/form/FormInputText";
// INTERFACES
import type { OpcUaCertificateConfigForm } from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Panel } = Collapse;
const { Option } = Select;

/** Human-readable labels for the generated SecurityPolicy enum. */
const SECURITY_POLICY_OPTIONS: { value: SecurityPolicy; label: string }[] = [
  { value: SecurityPolicy.None_, label: "None" },
  { value: SecurityPolicy.Basic128Rsa15, label: "Basic128Rsa15" },
  { value: SecurityPolicy.Basic256, label: "Basic256" },
  { value: SecurityPolicy.Basic256Sha256, label: "Basic256Sha256" },
  { value: SecurityPolicy.Aes128Sha256RsaOaep, label: "Aes128Sha256RsaOaep" },
  { value: SecurityPolicy.Aes256Sha256RsaPss, label: "Aes256Sha256RsaPss" },
];

/** Human-readable labels for the generated MessageSecurityMode enum. */
const SECURITY_MODE_OPTIONS: { value: MessageSecurityMode; label: string }[] = [
  { value: MessageSecurityMode.None_, label: "None" },
  { value: MessageSecurityMode.Sign, label: "Sign" },
  { value: MessageSecurityMode.SignAndEncrypt, label: "SignAndEncrypt" },
];

const BLANK_CERTIFICATE: OpcUaCertificateConfigForm = {
  applicationCertPath: "",
  privateKeyPath: "",
  trustListDir: "",
  rejectListDir: "",
  trustServerCertificate: false,
  createKeypairIfMissing: true,
  verifyServerHostname: true,
};

export default function SecurityEditor({
  securityPolicy,
  securityMode,
  certificate,
  onChangeSecurityPolicy,
  onChangeSecurityMode,
  onChangeCertificate,
}: {
  securityPolicy: SecurityPolicy;
  securityMode: MessageSecurityMode;
  certificate: OpcUaCertificateConfigForm | null;
  onChangeSecurityPolicy: (value: SecurityPolicy) => void;
  onChangeSecurityMode: (value: MessageSecurityMode) => void;
  onChangeCertificate: (value: OpcUaCertificateConfigForm | null) => void;
}) {
  const [t] = useTranslation("global");

  const requiresCertificate = securityMode !== MessageSecurityMode.None_;

  const onToggleCertificateEnabled = (enabled: boolean) => {
    onChangeCertificate(enabled ? (certificate ?? BLANK_CERTIFICATE) : null);
  };

  const onChangeCertificateField = (
    value: string | boolean,
    field: keyof OpcUaCertificateConfigForm,
  ) => {
    const base = certificate ?? BLANK_CERTIFICATE;
    onChangeCertificate({ ...base, [field]: value });
  };

  return (
    <div className="container mt-3">
      <div className="row">
        <div className="col-12 col-md-6 mt-3">
          <span className="generalStyles__inputFlexRequired">*</span>
          <span className="generalStyles__inputLabel">Security Policy</span>
          <Select
            className="generalStyles__width100"
            size="large"
            value={securityPolicy}
            onChange={onChangeSecurityPolicy}
            style={{ marginTop: 8 }}
          >
            {SECURITY_POLICY_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </div>
        <div className="col-12 col-md-6 mt-3">
          <span className="generalStyles__inputFlexRequired">*</span>
          <span className="generalStyles__inputLabel">Security Mode</span>
          <Select
            className="generalStyles__width100"
            size="large"
            value={securityMode}
            onChange={onChangeSecurityMode}
            style={{ marginTop: 8 }}
          >
            {SECURITY_MODE_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      {requiresCertificate && !certificate && (
        <span className="generalStyles__error" style={{ fontSize: "12px" }}>
          Sign / SignAndEncrypt requires a certificate configuration below.
        </span>
      )}
      <br />
      <Collapse>
        <Panel header={t("general.opcUaApplicationInstanceCertificate")} key="1">
          <div className="generalStyles__flex" style={{ marginBottom: 16 }}>
            <span className="generalStyles__inputLabel">Configure certificate</span>
            <Switch
              checked={certificate !== null}
              onChange={onToggleCertificateEnabled}
              style={{ marginLeft: 8 }}
            />
          </div>
          {certificate && (
            <div className="container">
              <div className="row">
                <div className="col-12 col-md-6 mt-3">
                  <FormInputText
                    inputValue={certificate.applicationCertPath}
                    inputLabelName="Application Cert Path"
                    inputOnChange={(value) =>
                      onChangeCertificateField(value, "applicationCertPath")
                    }
                    inputVariableName="applicationCertPath"
                    isRequired={false}
                    disabledInput={false}
                  />
                </div>
                <div className="col-12 col-md-6 mt-3">
                  <FormInputText
                    inputValue={certificate.privateKeyPath}
                    inputLabelName="Private Key Path"
                    inputOnChange={(value) => onChangeCertificateField(value, "privateKeyPath")}
                    inputVariableName="privateKeyPath"
                    isRequired={false}
                    disabledInput={false}
                  />
                </div>
                <div className="col-12 col-md-6 mt-3">
                  <FormInputText
                    inputValue={certificate.trustListDir}
                    inputLabelName="Trust List Directory"
                    inputOnChange={(value) => onChangeCertificateField(value, "trustListDir")}
                    inputVariableName="trustListDir"
                    isRequired={false}
                    disabledInput={false}
                  />
                </div>
                <div className="col-12 col-md-6 mt-3">
                  <FormInputText
                    inputValue={certificate.rejectListDir}
                    inputLabelName="Reject List Directory"
                    inputOnChange={(value) => onChangeCertificateField(value, "rejectListDir")}
                    inputVariableName="rejectListDir"
                    isRequired={false}
                    disabledInput={false}
                  />
                </div>
                <div className="col-12 col-md-4 mt-3">
                  <div className="generalStyles__flex">
                    <span className="generalStyles__inputLabel">Trust Server Certificate</span>
                    <Switch
                      checked={certificate.trustServerCertificate}
                      onChange={(checked) =>
                        onChangeCertificateField(checked, "trustServerCertificate")
                      }
                      style={{ marginLeft: 8 }}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-4 mt-3">
                  <div className="generalStyles__flex">
                    <span className="generalStyles__inputLabel">Create Keypair If Missing</span>
                    <Switch
                      checked={certificate.createKeypairIfMissing}
                      onChange={(checked) =>
                        onChangeCertificateField(checked, "createKeypairIfMissing")
                      }
                      style={{ marginLeft: 8 }}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-4 mt-3">
                  <div className="generalStyles__flex">
                    <span className="generalStyles__inputLabel">Verify Server Hostname</span>
                    <Switch
                      checked={certificate.verifyServerHostname}
                      onChange={(checked) =>
                        onChangeCertificateField(checked, "verifyServerHostname")
                      }
                      style={{ marginLeft: 8 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </Panel>
      </Collapse>
    </div>
  );
}
